import type { FC } from 'react';
import React, { useContext, Fragment } from 'react';
import type { IntlFormatters } from 'react-intl-next';
import { defineMessages, useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import { Box, Inline, xcss } from '@atlaskit/primitives';

import { ErrorDisplay } from '@confluence/error-boundary';
import type { RoutesContextType } from '@confluence/route-manager';
import { RoutesContext } from '@confluence/route-manager';
import { SpacePermissionsSharedHeader } from '@confluence/space-permissions/entry-points/SpacePermissionsSettingsTab';
import { ReverseTrialLozengeExperiment } from '@confluence/trial-components';
import { ConfluenceEdition } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';

import {
	useSelectedNavigationWebItems,
	isSpacePermissionsRoute,
	isHubPermissionsRoute,
} from '../helpers/webItem';
import { useSpaceSettingsWebItemSections } from '../web-item-sections/useSpaceSettingsWebItemSections';
import type { WebItemSectionsWithLozenge } from '../web-item-sections/SpaceSettingsWebItemSectionsInjector';

const i18n = defineMessages({
	spaceDetailsPageTitle: {
		id: 'space-settings.space-settings-navigation.space-details-page.title',
		defaultMessage: 'Space details',
		description: 'Page title for the space details setting page',
	},
});

const NavContainerStyles = xcss({
	width: '100%',
	position: 'relative',
});

const TitleAndFilterBarStyles = xcss({
	marginBlockEnd: 'space.400',
});

type GetTitleFromWebItemPayload = string | null | undefined;

const getTitleFromWebItem = ({
	webItemSection,
	itemCompleteKey,
}: {
	webItemSection: WebItemSectionsWithLozenge | null | undefined;
	itemCompleteKey: string | null | undefined;
}): GetTitleFromWebItemPayload => {
	const innerTitle = webItemSection?.items?.find(
		(item) => item?.completeKey === itemCompleteKey,
	)?.label;
	const outerTitle = webItemSection?.label;
	return innerTitle || outerTitle;
};

const getTitleFromPathname = ({
	locationPathname,
	formatMessage,
}: {
	locationPathname: string;
	formatMessage: IntlFormatters['formatMessage'];
}): string | undefined => {
	if (
		locationPathname.includes('viewspacesummary.action') ||
		locationPathname.includes('editspace.action') ||
		locationPathname.includes('editspacelabels.action')
	) {
		// This is a special case for the space details page, which doesn't have a web item section
		// now that it's been migrated to the new navigation. So we've hardcoded the title here until
		// we are able to uplift the page from the monolith, at which point it will no longer render
		// via this component.
		return formatMessage(i18n.spaceDetailsPageTitle);
	} else {
		return undefined;
	}
};

const getLozengeFromWebItem = ({
	webItemSection,
	itemCompleteKey,
}: {
	webItemSection: WebItemSectionsWithLozenge | null | undefined;
	itemCompleteKey: string | null | undefined;
}): React.ReactNode =>
	webItemSection?.items?.find((item) => item?.completeKey === itemCompleteKey)?.lozenge;

const permissionEditionMap = new Map<string, ConfluenceEdition>([
	['settings/permissions/users', ConfluenceEdition.STANDARD],
	['settings/permissions/groups', ConfluenceEdition.STANDARD],
	['settings/permissions/guests', ConfluenceEdition.STANDARD],
	['settings/permissions/anonymous', ConfluenceEdition.STANDARD],
	['settings/permissions/analytics', ConfluenceEdition.PREMIUM],
]);

// This function is used to get the edition for the lozenge based on the location path.
// It's used to determine if the user is eligible for a reverse trial lozenge experiment.
const getRtExperimentIncludedPages = (locationPath: string): React.ReactNode => {
	let matchedKey: string | undefined;
	let edition: ConfluenceEdition | undefined;

	for (const key of permissionEditionMap.keys()) {
		if (locationPath.includes(key)) {
			matchedKey = key;
			edition = permissionEditionMap.get(key);
			break;
		}
	}

	if (!edition || !matchedKey) {
		return null;
	}

	const touchpoint = matchedKey.replaceAll('/', '_');
	return (
		<ReverseTrialLozengeExperiment
			touchpointId={`confluence_space_${touchpoint}`}
			edition={edition}
		/>
	);
};

export type SpaceSettingsNavigationV2Props = {
	children?: (props: { withCompleteNav: boolean }) => React.ReactNode;
	spaceKey: string;
};

/**
 * Renders Confluence Space Settings V2 tab navigation, including Connect and Forge apps.
 * This navigation may be used both on legacy monolith SSR space settings pages (where
 * it is injected via React portal), and on fully React SPA space settings pages (currently
 * only the Forge app space settings page).
 * This component handles only rendering the navigation tabs, not page contents.
 */
export const SpaceSettingsNavigationV2: FC<SpaceSettingsNavigationV2Props> = ({
	children,
	spaceKey,
}) => {
	const { formatMessage } = useIntl();
	const { location, matchRoute } = useContext<RoutesContextType>(RoutesContext);
	const { loading, error, webItemSections } = useSpaceSettingsWebItemSections({
		spaceAlias: spaceKey,
	});

	const { innerNavigationWebItem, outerNavigationWebItem } = useSelectedNavigationWebItems({
		matchRoute,
		location,
		webItemSections,
		skip: loading || !!error,
	});

	if (loading) {
		return null;
	} else if (error) {
		// Something went wrong; report it
		return <ErrorDisplay error={error} />;
	}

	const isSpaceOrHubPermsPage =
		isSpacePermissionsRoute(matchRoute, location) || isHubPermissionsRoute(matchRoute, location);
	const matchingWebItemSection = webItemSections?.find(
		(section) => section?.id === outerNavigationWebItem?.id,
	);
	const titleFromWebItem = getTitleFromWebItem({
		webItemSection: matchingWebItemSection,
		itemCompleteKey: innerNavigationWebItem?.completeKey,
	});
	const titleFromPathname = getTitleFromPathname({
		locationPathname: location?.pathname || '',
		formatMessage,
	});

	const lozengeFromWebItem = getLozengeFromWebItem({
		webItemSection: matchingWebItemSection,
		itemCompleteKey: innerNavigationWebItem?.completeKey,
	});

	const lozengeFromPathname = getRtExperimentIncludedPages(location?.pathname || '');

	return (
		<Box xcss={NavContainerStyles}>
			<Fragment>
				<Box xcss={TitleAndFilterBarStyles}>
					<Heading size="large" as="h1">
						<Inline>
							{titleFromWebItem || titleFromPathname}
							{lozengeFromWebItem || lozengeFromPathname}
						</Inline>
					</Heading>
				</Box>
				{isSpaceOrHubPermsPage && !fg('confluence_rbac_spaces_beta_opt_in') && (
					<SpacePermissionsSharedHeader spaceKey={spaceKey} />
				)}
			</Fragment>
			{typeof children === 'function' ? children({ withCompleteNav: true }) : children}
		</Box>
	);
};
